/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact as AgGridReactRefProps } from 'ag-grid-react/lib/agGridReact';
import { Atom, PrimitiveAtom, WritableAtom, atom } from 'jotai';
import { pitRoadDataLastPitSelector } from 'jotai/pitRoad';
import { qualifyingLapDataMobileSelector } from 'jotai/qualifyingLapData';
import {
  indyRaceOrderTableColDefs,
  nascarRaceOrderTableColDefs,
  raceOrderTableColDefs,
} from 'jotai/raceOrderTable/RaceOrderTableColumns';
import { lapDataReplayCurrentLapSelector } from 'jotai/raceReplay';
import { selectedReferenceVehiclesColorSelectorFamily } from 'jotai/referenceVehicle';
import {
  activeSanctioningBodyFeedAtom,
  activeSanctioningBodyIdAtom,
  isIndyCarSanctioningBodySelector,
} from 'jotai/sanctioningBody';
import { activeSeriesIdSelector, activeSessionReplayEnabledSelector, activeSessionSelector } from 'jotai/session';
import { atomFamily } from 'jotai/utils';
import { isMobileWorkspaceAtom } from 'jotai/workspace';
import _ from 'lodash';
import { GapMetadata, LapData, OriginType, RunType, SanctioningBodyEnum } from 'models/viewModels';
import { lapDataServiceCollection } from 'services/stream/RSocket/LapDataServiceRSocket';
import {
  LapDataStreamInputProps,
  LapDataWithLastPit,
  LapDataWithReferenceObject,
  LeaderSelectorFamilyProps,
} from './LapDataObject';

let listenerId: string;

export const lapDataSelectorFamily = atomFamily<OriginType, Atom<LapData[]>>((dataSource: OriginType) =>
  atom<LapData[]>((get) => {
    const activeSanctioningBodyFeed = get(activeSanctioningBodyFeedAtom);
    const activeSanctioningBodyId = get(activeSanctioningBodyIdAtom);
    const sanctioningBodyEnum = activeSanctioningBodyId ?? SanctioningBodyEnum.UNKNOWN;
    const isIndy = get(isIndyCarSanctioningBodySelector);
    const isReplay = get(activeSessionReplayEnabledSelector);
    const sessionType = get(activeSessionSelector)?.sessionType;
    const lapData = !isReplay
      ? get(
          lapDataAtomFamily({
            dataSource: activeSanctioningBodyFeed ? activeSanctioningBodyFeed : dataSource,
            sanctioningBody: sanctioningBodyEnum,
          })
        )
      : get(lapDataReplayCurrentLapSelector);
    if (sessionType !== RunType.RACE) {
      if (sessionType === RunType.PRACTICE) {
        const sortedLapData = [...lapData];
        // Sort lapData by bestLapTimes
        sortedLapData.sort((a, b) => {
          return a.bestLapTime === 0 ? 1 : b.bestLapTime === 0 ? -1 : a.bestLapTime - b.bestLapTime;
        });
        return sortedLapData.map((data, index) => {
          return {
            ...data,
            deltaToTarget: data.bestLapTime === 0 ? 0 : sortedLapData[0].bestLapTime - data.bestLapTime,
            runningPosition: index + 1,
          };
        });
      } else {
        if (get(isMobileWorkspaceAtom) && !isIndy) {
          // if it's mobile then we want to get the qualifying data with simplified runNames (ex: Group A -> A)
          const qualifyingLapData = get(qualifyingLapDataMobileSelector);
          return qualifyingLapData.map((data) => {
            return {
              ...data,
              lapsLed: 0,
              movingAverageLapTime: 0,
              lapsToTarget: 0,
              avgLapTime: 0,
              fastRank: 0,
              avgRank: 0,
              gapmetadataList: [],
              // unknown is to allow the missing properties to not cause type errors
            } as unknown as LapData;
          });
        }
        return lapData;
      }
    } else {
      const lastPitData = get(pitRoadDataLastPitSelector);
      if (lastPitData !== undefined) {
        return lapData.map((data) => {
          return { ...data, ...lastPitData[data.vehicleNumber] } as LapDataWithLastPit;
        }, []);
      } else {
        return lapData;
      }
    }
  })
);

export const lapDataWithReferenceSelectorFamily = atomFamily<OriginType, Atom<LapDataWithReferenceObject[]>>(
  (dataSource: OriginType) =>
    atom<LapDataWithReferenceObject[]>((get) => {
      const data = get(lapDataSelectorFamily(dataSource));
      const seriesId = get(activeSeriesIdSelector);
      const referenceVehicleObj = get(selectedReferenceVehiclesColorSelectorFamily(seriesId || ''));
      const referenceVehicle = data.find((item) => item.vehicleNumber === referenceVehicleObj?.vehicleNumber);
      const gapMetadataList = referenceVehicle?.gapMetadataList;

      return data.map((item) => {
        if (referenceVehicle && gapMetadataList) {
          item = updateReferenceVehicleGapMetadata(item, referenceVehicle, gapMetadataList);
        }
        return { ...item, referenceVehicle: referenceVehicleObj?.vehicleNumber } as LapDataWithReferenceObject;
      });
    })
);

const currentLapDataAtomFamily = atomFamily<LapDataStreamInputProps, PrimitiveAtom<LapData[]>>(
  (input: LapDataStreamInputProps) => atom<LapData[]>([]),
  (a, b) => _.isEqual(a, b)
);

const initLapDataAtomFamily = atomFamily<LapDataStreamInputProps, WritableAtom<null, [], () => void>>(
  (input: LapDataStreamInputProps) => {
    const newAtom = atom<null, [], () => void>(null, (_get, set) => {
      const { dataSource, sanctioningBody } = input;
      listenerId = lapDataServiceCollection
        .get(sanctioningBody)!
        .get(dataSource)!
        .addListener(
          async (lapData) => {
            set(currentLapDataAtomFamily(input), lapData);
          },
          async () => {
            set(currentLapDataAtomFamily(input), [] as LapData[]);
          }
        );
      return () => {
        lapDataServiceCollection.get(sanctioningBody)?.get(dataSource)?.removeListener(listenerId);
      };
    });
    newAtom.onMount = (init) => {
      init();
    };
    return newAtom;
  },
  (a, b) => _.isEqual(a, b)
);

export const lapDataAtomFamily = atomFamily<LapDataStreamInputProps, Atom<LapData[]>>(
  (input: LapDataStreamInputProps) =>
    atom<LapData[]>((get) => {
      get(initLapDataAtomFamily(input));
      return get(currentLapDataAtomFamily(input));
    }),
  (a, b) => _.isEqual(a, b)
);

/**
 * Maps the leader vehicles from the given dataSource to their vehicleNumbers
 */
export const leaderVehicleNumbersSelectorFamily = atomFamily<LeaderSelectorFamilyProps, Atom<string[]>>(
  (props: LeaderSelectorFamilyProps) =>
    atom<string[]>((get) => {
      const leaders = get(leaderVehiclesSelectorFamily(props));
      return leaders.map((leader) => leader.vehicleNumber);
    }),
  (a, b) => _.isEqual(a, b)
);

/**
 * Returns the leader vehicles for the given dataSource
 */
const leaderVehiclesSelectorFamily = atomFamily<LeaderSelectorFamilyProps, Atom<LapData[]>>(
  (props: LeaderSelectorFamilyProps) =>
    atom<LapData[]>((get) => {
      const lapData = get(lapDataSelectorFamily(props.dataSource));
      return lapData.reduce((previousValue, currentValue) => {
        if (currentValue.runningPosition <= props.lowestLeadingPosition && !previousValue.includes(currentValue)) {
          previousValue.push(currentValue);
        }
        return previousValue;
      }, [] as LapData[]);
    }),
  (a, b) => _.isEqual(a, b)
);

export const currentFasterDriverSelectorFamily = atomFamily<OriginType, Atom<string>>((dataSource: OriginType) =>
  atom<string>((get) => {
    const lapData = get(lapDataSelectorFamily(dataSource));
    let lowestLapTime = Number.MAX_SAFE_INTEGER;
    return lapData.reduce((previousValue, currentValue) => {
      if (currentValue.lastLapTime < lowestLapTime && currentValue.lastLapTime > 0) {
        previousValue = currentValue.vehicleNumber;
        lowestLapTime = currentValue.lastLapTime;
      }
      return previousValue;
    }, '');
  })
);

export const lapDataLuckyDogSelectorFamily = atomFamily<OriginType, Atom<string>>((dataSource: OriginType) =>
  atom<string>((get) => {
    const lapData = get(lapDataSelectorFamily(dataSource));
    const leader = get(leaderVehiclesSelectorFamily({ dataSource, lowestLeadingPosition: 1 }))[0];
    // return the lowest(highest) positioned vehicle with a lapsToTarget > 0
    const luckyDog = lapData.reduce((currentDog, lapData) => {
      if (
        lapData &&
        leader &&
        (lapData.lapsToTarget ?? 0) > 0 &&
        (currentDog === undefined ||
          lapData.runningPosition < currentDog.runningPosition ||
          lapData.lapsCompleted > currentDog.lapsCompleted)
      ) {
        currentDog = lapData;
      }
      return currentDog;
    }, undefined as LapData | undefined);
    return luckyDog ? luckyDog.vehicleNumber : '';
  })
);

const updateReferenceVehicleGapMetadata = (
  item: LapData,
  referenceVehicle: LapData,
  gapMetadataList: Array<GapMetadata>
) => {
  if (item.runningPosition > referenceVehicle.runningPosition && item.lapsToTarget === referenceVehicle.lapsToTarget) {
    return item;
  }
  let filteredGapMetadataList = gapMetadataList.filter(
    (gapData) => gapData.vehicleNumber !== referenceVehicle.vehicleNumber
  );
  const newGapData = {
    vehicleNumber: referenceVehicle.vehicleNumber,
    gap: -(gapMetadataList.find((gapData) => gapData.vehicleNumber === item.vehicleNumber)?.gap || 0),
  };
  if (item.lapsToTarget === referenceVehicle.lapsToTarget) {
    filteredGapMetadataList = [...filteredGapMetadataList, newGapData];
  }

  return {
    ...item,
    gapmetadataList: filteredGapMetadataList,
  };
};

export const mobileRaceOrderAgGridRefAtom = atom<AgGridReactRefProps | null>(null);

export const raceOrderTableColumnsSelector = atom<ColDef[]>((get) => {
  const sanctioningBody = get(activeSanctioningBodyIdAtom);
  switch (sanctioningBody) {
    case SanctioningBodyEnum.INDYCAR: {
      return indyRaceOrderTableColDefs;
    }
    case SanctioningBodyEnum.NASCAR:
      return nascarRaceOrderTableColDefs;
    default:
      return raceOrderTableColDefs;
  }
});

export const getMobileColumnHeaders = (headerValue: string | undefined) => {
  switch (headerValue) {
    case 'Vehicle':
      return 'Veh';
    case 'Fast Lap':
      return 'FastLT';
    default:
      return headerValue;
  }
};
